import { Component, ElementRef, inject, ViewChild, OnInit, OnDestroy, signal } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { StyleClassModule } from 'primeng/styleclass';
import { LayoutService } from '@/core/layout/service/layout.service';
import { ButtonModule } from 'primeng/button';
import { FormsModule } from '@angular/forms';
import { BadgeModule } from 'primeng/badge';
import { MenuModule } from 'primeng/menu';
import { Menu } from 'primeng/menu';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import { selectUserProfile } from '@/store/current-user/current-user.selectors';
import { ReturnNavigationService } from '@/shared/services/return-navigation.service';

@Component({
  selector: '[app-topbar]',
  standalone: true,
  imports: [RouterModule, CommonModule, StyleClassModule, FormsModule, ButtonModule, BadgeModule, MenuModule],
  template: `
    <div class="layout-topbar-start">
      <div class="layout-topbar-logo">
        <img src="/envolve/logo.png" alt="Envolve" class="logo-image" />
        <span class="logo-text">Envolve</span>
      </div>
      <a #menuButton class="layout-menu-button" (click)="onMenuButtonClick()">
        <i class="pi pi-chevron-right"></i>
      </a>
    </div>

    <div class="layout-topbar-end">
      <div class="layout-topbar-actions-start">
        <!--
          ALTERNATIVE APPROACH: If more topbar items are added, consider using a mobile toggle button instead.
          The Ultima theme has built-in support for this:
          1. Add <a class="layout-topbar-mobile-button" (click)="onTopbarMenuToggle()"><i class="pi pi-ellipsis-v"></i></a>
             to layout-topbar-start (after layout-menu-button)
          2. Remove the current responsive override approach
          3. The theme CSS will handle showing all topbar items in a dropdown on mobile
          See _responsive.scss lines 125-165 for the layout-topbar-menu-active behavior
        -->
        <a routerLink="/" class="workspace-link">My Workspace</a>
      </div>
      <div class="layout-topbar-actions-end">
        <ul class="layout-topbar-items">
          <li *ngIf="isAuthenticated()">
            <button
              #createBtn
              pButton
              type="button"
              label="Create"
              icon="pi pi-plus"
              class="p-button-sm create-button"
              (click)="createMenu.toggle($event)"
            ></button>
            <p-menu #createMenu [model]="createMenuItems" [popup]="true" [appendTo]="'body'"></p-menu>
          </li>
          <!-- This area is good for actions: -->
          <!-- Search bar, Notifications bell, App Selector, Right menu open -->
          <!-- Top Nav profile with links to log out  -->
        </ul>
      </div>
    </div>
  `,
  host: {
    class: 'layout-topbar',
  },
  styles: `
    :host ::ng-deep .p-overlaybadge .p-badge {
      outline-width: 0px;
    }

    .layout-topbar-logo {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem 1rem;
    }

    .logo-image {
      height: 32px;
      width: auto;
      object-fit: contain;
    }

    .logo-text {
      font-size: 1.25rem;
      font-weight: 600;
      color: #fff;
      letter-spacing: -0.025em;
    }

    @media (max-width: 768px) {
      .logo-text {
        display: none;
      }

      .logo-image {
        height: 28px;
      }
    }

    /* Workspace link styling */
    .workspace-link {
      color: var(--topbar-item-text-color, #fff);
      text-decoration: none;
      font-weight: 500;
      padding: 0.5rem 1rem;
      border-radius: var(--border-radius, 6px);
      transition: background-color 0.2s;

      &:hover {
        background-color: var(--topbar-item-hover-bg, rgba(255,255,255,0.1));
      }
    }

    /* Mobile responsive styles (992px matches Ultima theme $breakpoint) */
    @media (max-width: 991px) {
      /* Hide Create button label on mobile, keep icon */
      :host ::ng-deep .create-button {
        .p-button-label {
          display: none;
        }

        .p-button-icon {
          margin-right: 0;
        }
      }

      /* Override theme default that hides layout-topbar-end on mobile */
      :host {
        &.layout-topbar {
          flex-direction: row !important;
          height: 4rem;
        }

        .layout-topbar-end {
          display: flex !important;
          flex-grow: 1;
        }

        .layout-topbar-start {
          width: auto;
          flex-shrink: 0;
        }
      }
    }

  `,
})
export class AppTopbar implements OnInit, OnDestroy {
  layoutService = inject(LayoutService);
  router = inject(Router);
  private store = inject(Store);
  private destroy$ = new Subject<void>();
  private returnNavigationService = inject(ReturnNavigationService);

  // Authentication state
  isAuthenticated = signal<boolean>(false);

  @ViewChild('menuButton') menuButton!: ElementRef<HTMLButtonElement>;

  @ViewChild('createMenu') createMenu!: Menu;

  createMenuItems: MenuItem[] = [
    {
      label: 'Opportunity',
      icon: 'pi pi-bolt',
      command: () => {
        this.router.navigate(['/apps/opportunity-management/opportunity/add'], {
          queryParams: { returnUrl: this.returnNavigationService.getCurrentUrlForReturn() },
        });
      },
    },
    {
      label: 'Task',
      icon: 'pi pi-check-square',
      command: () => {
        this.router.navigate(['/apps/opportunity-management/task/add'], {
          queryParams: { returnUrl: this.returnNavigationService.getCurrentUrlForReturn() },
        });
      },
    },
  ];

  ngOnInit(): void {
    // Subscribe to user profile changes to determine authentication state
    this.store
      .select(selectUserProfile)
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        // User is authenticated if profile exists
        this.isAuthenticated.set(!!user);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onMenuButtonClick() {
    this.layoutService.onMenuToggle();
  }

  onTopbarMenuToggle() {
    this.layoutService.layoutState.update((val) => ({
      ...val,
      topbarMenuActive: !val.topbarMenuActive,
    }));
  }
}
